import { useState, useEffect, useCallback } from "react";
import { Wallet, Download, Gift, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtARS = (n) => `$${Number(n || 0).toLocaleString("es-AR")}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return "—";
  }
};

/**
 * MyEarningsPanel - "Mis cobros" del Consultorio Privado.
 * Lista las consultas privadas cobradas, con desglose honorario/cargo de
 * servicio, totales y export a CSV. Fase 1: los pagos son simulados por lo
 * que los registros aparecen a medida que las consultas se confirman.
 */
const MyEarningsPanel = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_URL}/api/doctors/me/earnings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error("No pudimos cargar tus cobros");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const handleExportCSV = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_URL}/api/doctors/me/earnings/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mis-cobros-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) {
      console.error(e);
      toast.error("Error al exportar");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 pr-8">
            <Wallet className="w-5 h-5 text-[#1d366b] flex-shrink-0" />
            <span className="truncate">Mis cobros — Consultorio Privado</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d366b]"></div>
          </div>
        ) : (
          <div className="space-y-5 px-6 py-5 overflow-y-auto" data-testid="my-earnings-panel">
            {/* Totals */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                  Honorarios cobrados
                </p>
                <p className="text-2xl font-bold text-emerald-800 mt-1" data-testid="earnings-honorarios">
                  {fmtARS(data?.total_honorario)}
                </p>
                <p className="text-xs text-emerald-700 mt-0.5">{data?.count || 0} consulta(s)</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-600 font-semibold">
                  Cargo de servicio
                </p>
                <p className="text-2xl font-bold text-slate-800 mt-1" data-testid="earnings-fees">
                  {fmtARS(data?.total_service_fee)}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">Lo paga el paciente</p>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">
                  Total procesado
                </p>
                <p className="text-2xl font-bold text-blue-800 mt-1" data-testid="earnings-gross">
                  {fmtARS(data?.total_gross)}
                </p>
                <p className="text-xs text-blue-700 mt-0.5">Todos los pagos</p>
              </div>
            </div>

            {/* Free promo banner */}
            {typeof data?.free_consults_remaining === "number" && data.free_consults_remaining > 0 && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3 rounded-lg" data-testid="earnings-promo-banner">
                <Gift className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Te quedan <strong>{data.free_consults_remaining}</strong> consulta(s)
                  con <strong>0% de cargo de servicio</strong> (promo primeras 5).
                </span>
              </div>
            )}

            {/* Export */}
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Los cobros se registran al confirmarse la consulta.
              </p>
              <Button
                variant="outline"
                onClick={handleExportCSV}
                disabled={downloading || !data?.count}
                data-testid="earnings-export-csv-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                {downloading ? "Descargando..." : "Exportar CSV"}
              </Button>
            </div>

            {/* Table */}
            {data?.count > 0 ? (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="max-h-[45vh] overflow-y-auto overflow-x-auto">
                  <table className="w-full min-w-[460px] text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Fecha</th>
                        <th className="px-3 py-2 font-semibold">Paciente</th>
                        <th className="px-3 py-2 font-semibold text-right">Honorario</th>
                        <th className="px-3 py-2 font-semibold text-right">Cargo</th>
                        <th className="px-3 py-2 font-semibold text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((it) => (
                        <tr
                          key={it.payment_id}
                          className="border-b border-slate-100 hover:bg-slate-50/60"
                          data-testid="earnings-row"
                        >
                          <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                            {fmtDate(it.paid_at)}
                          </td>
                          <td className="px-3 py-2 text-slate-900">
                            <div className="font-medium">{it.patient_name || "—"}</div>
                            {it.category && (
                              <div className="text-xs text-slate-500">{it.category}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-900 font-medium">
                            {fmtARS(it.honorario)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {it.fee_free ? (
                              <span className="text-emerald-700 text-xs font-semibold">Promo</span>
                            ) : (
                              fmtARS(it.service_fee)
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-900 font-semibold">
                            {fmtARS(it.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-10 border border-dashed border-slate-200 rounded-lg" data-testid="earnings-empty-state">
                <TrendingUp className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  Todavía no cobraste ninguna consulta privada.
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Compartí tu enlace privado y los cobros aparecerán acá.
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MyEarningsPanel;
